import {
  Avatar,
  Badge,
  Button,
  Divider,
  ListItemAvatar,
  List as MuiList,
  Stack,
  Typography,
  badgeClasses,
  listItemAvatarClasses,
  listItemClasses,
  listItemSecondaryActionClasses,
  listItemTextClasses,
  styled,
} from '@mui/material'
import { type Model } from 'jms-domain'
import keyBy from 'lodash-es/keyBy'
import { useCallback, useMemo, useState } from 'react'

import type { DomainEntityId } from '@salsita-ai/platform-project-sdk/domain'
import {
  SimpleRecipePanel,
  VerticalGroupRecipePanel,
  useGroupedRecipeOptions,
  useResourceUrl,
} from '@salsita-ai/platform-project-sdk/react'
import { ListItem, PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { configuratorInterface } from '../systems/configurator-interface'

const List = styled(MuiList)(({ theme: { palette, spacing } }) => ({
  display: 'grid',
  gap: spacing(0.5),
  padding: 0,

  [`.${listItemClasses.root}`]: {
    gap: spacing(1.5),

    '&[class]': {
      padding: `${spacing(0.5)} ${spacing(1)}`,
    },

    // TODO: eimplement once the refactored `Recipe.Item` is available.
    // Temporary visual workaround simulating the `ListItem.Static` variant.
    '&:has([data-badge="0"])': {
      pointerEvents: 'none',
    },
  },
  [`.${listItemAvatarClasses.root}`]: {
    minWidth: 0,
  },
  [`.${listItemTextClasses.root}`]: {
    margin: 0,
  },
  [`.${listItemSecondaryActionClasses.root}`]: {
    blockSize: '100%',
    display: 'grid',

    '&[class][class]': {
      padding: 0,
    },
  },
  [`.${badgeClasses.root}`]: {
    position: 'static',
    transform: 'none',
  },
  [`.${badgeClasses.badge}`]: {
    background: palette.grey[300],
    position: 'static',
    transform: 'none',
    translate: '0 2px',
  },
}))

interface RecipesProps {
  id: Model.Room['id']
}

const rootLevelGroups = [
  {
    name: 'Cabinets',
    description: 'Cabinets in different sizes',
    recipeGroups: ['drawer33Door66', 'threeDrawerBase', 'fullDoorBase', 'sinkBaseCabinets', 'trashPulloutBase'],
    thumbnailUrl: '/images/product-images/thumbnails/1-3-drawer-2-3-door.png',
  },
  {
    name: 'Grills',
    description: 'Lynx grills',
    recipeGroups: ['lynxProfessionalGrill', 'lynxSedonaGrill', 'lynxEvo'],
    thumbnailUrl: '/images/product-images/thumbnails/grill-base.png',
  },
  {
    name: 'Burners',
    description: 'Burners in different sizes',
    recipeGroups: ['lynxProfessionalBurner', 'sedonaBurner'],
    thumbnailUrl: '/images/product-images/thumbnails/sink-base.png',
  },
  {
    name: 'Other cooking items',
    description: 'Ovens, Evo, Asado, Kamado Joe',
    recipeGroups: ['otherCookingItems'],
    thumbnailUrl: '/images/product-images/thumbnails/ceramic-grill-base.png',
  },
  {
    name: 'Hoods',
    description: 'Hoods for your cooking items',
    recipeGroups: ['lynxSedonaHood'],
    thumbnailUrl: '/images/product-images/thumbnails/lynx-pro-36-vent-hood.png',
  },
  {
    name: 'Sinks',
    description: 'Lynx sinks',
    recipeGroups: ['lynxSink'],
    thumbnailUrl: '/images/product-images/thumbnails/sink.png',
  },
  {
    name: 'Refrigeration',
    description: 'Fridges, freezers, and wine cellars',
    recipeGroups: ['refrigeration'],
    thumbnailUrl: '/images/product-images/thumbnails/fridge_white.png',
  },
  {
    name: 'Additional parts',
    description: 'Optional parts you might need',
    recipeGroups: ['fillers', 'endPanels'],
    thumbnailUrl: '/images/product-images/thumbnails/1-3-drawer-2-3-door.png',
  },
] as const

const RecipeGroups = (props: { entityId: DomainEntityId<string>; onClick: (groupIndex: number) => void }) => {
  const { entityId, onClick } = props

  const { getResourceUrl } = useResourceUrl()
  const recipes = useGroupedRecipeOptions(entityId)
  const groupsWithCount = useMemo(() => {
    const recipesByInstanceName = keyBy(recipes, 'instanceName')
    const counts = rootLevelGroups.map(group => {
      const count = group.recipeGroups.reduce((acc, group) => {
        const recipeGroup = recipesByInstanceName[group]
        if (recipeGroup) {
          return acc + recipeGroup.recipes.length
        }
        return acc
      }, 0)

      return { ...group, count }
    })

    return counts
  }, [recipes])

  return (
    <Stack sx={{ gap: 2.5 }}>
      <Typography variant="h5">Components</Typography>
      <Divider />
      <List>
        {groupsWithCount.map(({ name, description, thumbnailUrl, count }, groupIndex) => (
          <ListItem.ClickableLayout
            key={name}
            title={name}
            subtitle={description}
            primarySlot={
              <ListItemAvatar>
                <Avatar src={undefined} variant="square">
                  <img src={getResourceUrl(thumbnailUrl)} alt={name} style={{ width: '100%', height: '100%' }} />
                </Avatar>
              </ListItemAvatar>
            }
            secondarySlot={<Badge variant="standard" badgeContent={String(count)} data-badge={String(count)} />}
            onClick={() => {
              onClick(groupIndex)
            }}
          />
        ))}
      </List>
    </Stack>
  )
}

interface RecipeGroupDetailProps {
  entityId: DomainEntityId<string>
  group: (typeof rootLevelGroups)[number]
  onBack: () => void
}

const RecipesGroupDetail = (props: RecipeGroupDetailProps) => {
  const { entityId, group, onBack } = props

  const selectedRecipeInstanceNames = useMemo(() => (group ? [...group.recipeGroups] : []), [group])

  return (
    <div>
      <Button size="large" startIcon={<PhxIcon name="ArrowLeft" />} sx={{ mt: -1, mb: 4 }} onClick={onBack}>
        Back
      </Button>
      <Stack sx={{ gap: 2.5 }}>
        <Stack gap={1}>
          <Typography variant="h5">{group.name}</Typography>
          <Typography variant="body1" color="text.secondary">
            {group.description}
          </Typography>
        </Stack>
        <Divider />
        <Stack gap={2}>
          {group.recipeGroups.length > 1 ? (
            <VerticalGroupRecipePanel entityId={entityId} instanceNames={selectedRecipeInstanceNames} />
          ) : (
            <SimpleRecipePanel entityId={entityId} instanceNames={selectedRecipeInstanceNames} />
          )}
        </Stack>
      </Stack>
    </div>
  )
}

export const Recipes = (props: RecipesProps) => {
  const { id } = props

  const { navigationSystem, storeSystem } = configuratorInterface.useDomain()
  const state = storeSystem.stateSlices.committed.value.state

  const [selectedGroupIndex, setSelectedGroupIndex] = useState<number | null>(null)
  const selectedGroup = rootLevelGroups[selectedGroupIndex ?? -1]

  const handleBack = useCallback(() => {
    const popUpdate = navigationSystem.getPopNavigationUpdate(state)
    if (!popUpdate) {
      return
    }

    storeSystem.stageAndCommit([popUpdate])
  }, [navigationSystem, storeSystem, state])

  return (
    <div>
      {selectedGroup?.recipeGroups.length ? (
        <RecipesGroupDetail
          entityId={id}
          group={selectedGroup}
          onBack={() => {
            setSelectedGroupIndex(null)
          }}
        />
      ) : (
        <>
          <Button size="large" startIcon={<PhxIcon name="ArrowLeft" />} sx={{ mt: -1, mb: 4 }} onClick={handleBack}>
            Back
          </Button>
          <RecipeGroups entityId={id} onClick={setSelectedGroupIndex} />
        </>
      )}
    </div>
  )
}
